import { apiFetchJson } from './apiClient';
import type { ApiChannel, ApiMessage, ApiReply, ApiSection, ApiTopic } from './communityApiMappers';
import type { AccessSetting, SectionStatus, SectionType, TopicStatus, ViewAccessSetting } from '../components/Community/types';

export type { ApiChannel, ApiMessage, ApiReply, ApiSection, ApiTopic };

/** Два фиксированных раздела сайдбара (= `community_channels.category`). */
export const COMMUNITY_CHANNEL_SECTIONS = [
  { id: 'information' as const, title: 'Информация' },
  { id: 'text' as const, title: 'Текстовые каналы' },
];

export type CommunityChannelSectionId = (typeof COMMUNITY_CHANNEL_SECTIONS)[number]['id'];

export type CommunityChannel = {
  id: string;
  name: string;
  category: CommunityChannelSectionId;
  channel_type: 'chat' | 'forum';
  locked?: boolean;
};

export type CommunityChannelsResponse = {
  data: CommunityChannel[];
  meta: { isCommunityAdmin: boolean };
};

export type CommunityChannelCreateResponse = {
  data: CommunityChannel;
};

export type CommunityMessage = {
  id: string;
  channel_id: string;
  author: {
    id: string;
    name: string;
  };
  body: string;
  created_at: string;
};

export type CommunityMessagesResponse = {
  data: CommunityMessage[];
};

export type CommunityMessageCreateResponse = {
  data: CommunityMessage;
};

export async function fetchCommunityChannels(): Promise<
  | { ok: true; data: CommunityChannel[]; isCommunityAdmin: boolean }
  | { ok: false; error: string; status: number }
> {
  const res = await apiFetchJson<CommunityChannelsResponse>('GET', '/private-circle/community/channels');
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  const list: CommunityChannel[] = (res.data.data || []).map((c) => ({
    id: c.id,
    name: c.name,
    category: c.category === 'information' || c.category === 'text' ? c.category : 'text',
    channel_type: c.channel_type === 'forum' ? 'forum' : 'chat',
    locked: c.locked,
  }));
  return {
    ok: true,
    data: list,
    isCommunityAdmin: Boolean(res.data.meta?.isCommunityAdmin),
  };
}

export async function createCommunityChannel(
  name: string,
  category: CommunityChannelSectionId,
  channelType: 'chat' | 'forum' = 'chat',
): Promise<{ ok: true; channel: CommunityChannel } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<CommunityChannelCreateResponse>('POST', '/private-circle/community/channels', {
    body: { name, category, channel_type: channelType },
  });
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  const c = res.data.data;
  const channel: CommunityChannel = {
    id: c.id,
    name: c.name,
    category: c.category === 'information' || c.category === 'text' ? c.category : 'text',
    channel_type: c.channel_type === 'forum' ? 'forum' : 'chat',
  };
  return { ok: true, channel };
}

export async function fetchCommunityStructure(): Promise<
  | { ok: true; sections: ApiSection[]; channels: ApiChannel[]; isCommunityAdmin: boolean }
  | { ok: false; error: string; status: number }
> {
  const res = await apiFetchJson<{ data: { sections: ApiSection[]; channels: ApiChannel[] }; meta: { isCommunityAdmin: boolean } }>(
    'GET',
    '/private-circle/community/structure',
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return {
    ok: true,
    sections: res.data.data?.sections || [],
    channels: res.data.data?.channels || [],
    isCommunityAdmin: Boolean(res.data.meta?.isCommunityAdmin),
  };
}

export async function createCommunitySection(input: {
  title: string;
  sort_order?: number;
  status?: SectionStatus;
}): Promise<{ ok: true; section: ApiSection } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ data: ApiSection }>('POST', '/private-circle/community/sections', { body: input });
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true, section: res.data.data };
}

export async function updateCommunitySection(
  sectionId: string,
  input: Partial<{ title: string; sort_order: number; status: SectionStatus }>,
): Promise<{ ok: true; section: ApiSection } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ data: ApiSection }>('PATCH', `/private-circle/community/sections/${encodeURIComponent(sectionId)}`, { body: input });
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true, section: res.data.data };
}

export async function reorderCommunitySections(
  orderedIds: string[],
): Promise<{ ok: true } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ ok: boolean }>('POST', '/private-circle/community/sections/reorder', {
    body: { ordered_ids: orderedIds },
  });
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true };
}

export async function deleteCommunitySection(
  sectionId: string,
): Promise<{ ok: true } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ ok: boolean }>('DELETE', `/private-circle/community/sections/${encodeURIComponent(sectionId)}`);
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true };
}

export async function updateCommunityChannel(
  channelId: string,
  input: Record<string, unknown>,
): Promise<{ ok: true; channel: ApiChannel } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ data: ApiChannel }>(
    'PATCH',
    `/private-circle/community/channels/${encodeURIComponent(channelId)}`,
    { body: input },
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true, channel: res.data.data };
}

export async function reorderCommunityChannels(
  orderedIds: string[],
): Promise<{ ok: true } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ ok: boolean }>('POST', '/private-circle/community/channels/reorder', {
    body: { ordered_ids: orderedIds },
  });
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true };
}

export async function deleteCommunityChannel(
  channelId: string,
): Promise<{ ok: true } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ ok: boolean }>('DELETE', `/private-circle/community/channels/${encodeURIComponent(channelId)}`);
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true };
}

export async function createCommunityChannelFull(input: {
  name: string;
  section_id: string;
  channel_type: 'chat' | 'forum';
  category?: 'information' | 'text';
  description?: string;
  icon?: string;
  forum_type?: SectionType;
  access_mode?: ViewAccessSetting;
  post_access?: AccessSetting;
  create_topics_access?: AccessSetting;
  reply_access?: AccessSetting;
}): Promise<{ ok: true; channel: ApiChannel } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ data: ApiChannel }>('POST', '/private-circle/community/channels', {
    body: {
      ...input,
      access_rules: {
        post_access: input.post_access || 'all',
        create_topics_access: input.create_topics_access || 'all',
        reply_access: input.reply_access || 'all',
      },
    },
  });
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true, channel: res.data.data };
}

export async function fetchCommunityTopics(
  channelId: string,
  since?: string,
): Promise<{ ok: true; topics: ApiTopic[] } | { ok: false; error: string; status: number }> {
  const qs = since ? `?since=${encodeURIComponent(since)}` : '';
  const res = await apiFetchJson<{ data: ApiTopic[] }>(
    'GET',
    `/private-circle/community/channels/${encodeURIComponent(channelId)}/topics${qs}`,
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true, topics: res.data.data || [] };
}

export async function createCommunityTopic(
  channelId: string,
  input: { title: string; description: string; tags?: string[]; anonymous?: boolean },
): Promise<{ ok: true; topic: ApiTopic } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ data: ApiTopic }>(
    'POST',
    `/private-circle/community/channels/${encodeURIComponent(channelId)}/topics`,
    { body: input },
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true, topic: res.data.data };
}

export async function updateCommunityTopic(
  topicId: string,
  input: Partial<{ status: TopicStatus; title: string; summary: { body: string; prepared_by?: string } }>,
): Promise<{ ok: true; topic: ApiTopic } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ data: ApiTopic }>(
    'PATCH',
    `/private-circle/community/topics/${encodeURIComponent(topicId)}`,
    { body: input },
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true, topic: res.data.data };
}

export async function deleteCommunityTopic(
  topicId: string,
): Promise<{ ok: true } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ ok: boolean }>('DELETE', `/private-circle/community/topics/${encodeURIComponent(topicId)}`);
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true };
}

export async function fetchCommunityReplies(
  topicId: string,
  since?: string,
): Promise<{ ok: true; replies: ApiReply[] } | { ok: false; error: string; status: number }> {
  const qs = since ? `?since=${encodeURIComponent(since)}` : '';
  const res = await apiFetchJson<{ data: ApiReply[] }>(
    'GET',
    `/private-circle/community/topics/${encodeURIComponent(topicId)}/replies${qs}`,
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true, replies: res.data.data || [] };
}

export async function createCommunityReply(
  topicId: string,
  input: { body: string; anonymous?: boolean },
): Promise<{ ok: true; reply: ApiReply } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ data: ApiReply }>(
    'POST',
    `/private-circle/community/topics/${encodeURIComponent(topicId)}/replies`,
    { body: input },
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true, reply: res.data.data };
}

export async function updateCommunityReply(
  replyId: string,
  body: string,
): Promise<{ ok: true; reply: ApiReply } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ data: ApiReply }>(
    'PATCH',
    `/private-circle/community/replies/${encodeURIComponent(replyId)}`,
    { body: { body } },
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true, reply: res.data.data };
}

export async function deleteCommunityReply(
  replyId: string,
): Promise<{ ok: true } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ ok: boolean }>('DELETE', `/private-circle/community/replies/${encodeURIComponent(replyId)}`);
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true };
}

export async function fetchCommunityMessages(
  channelId: string,
  since?: string,
): Promise<{ ok: true; messages: ApiMessage[] } | { ok: false; error: string; status: number }> {
  const qs = since ? `?since=${encodeURIComponent(since)}` : '';
  const res = await apiFetchJson<{ data: ApiMessage[] }>(
    'GET',
    `/private-circle/community/channels/${encodeURIComponent(channelId)}/messages${qs}`,
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true, messages: Array.isArray(res.data.data) ? res.data.data : [] };
}

export async function createCommunityMessage(
  channelId: string,
  body: string,
  anonymous = false,
): Promise<{ ok: true; message: ApiMessage } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ data: ApiMessage }>(
    'POST',
    `/private-circle/community/channels/${encodeURIComponent(channelId)}/messages`,
    { body: { body, anonymous } },
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true, message: res.data.data };
}

export async function updateCommunityMessage(
  channelId: string,
  messageId: string,
  body: string,
): Promise<{ ok: true; message: ApiMessage } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ data: ApiMessage }>(
    'PATCH',
    `/private-circle/community/channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(messageId)}`,
    { body: { body } },
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true, message: res.data.data };
}

export async function deleteCommunityMessage(
  channelId: string,
  messageId: string,
): Promise<{ ok: true } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ ok: boolean }>(
    'DELETE',
    `/private-circle/community/channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(messageId)}`,
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true };
}

export type CommunityQuestionnaireFilter = 'all' | 'yes' | 'no' | 'pending';

export type CommunityMemberForAccess = {
  userId: string;
  email: string;
  label: string;
  pseudonym: string | null;
  company: string | null;
  sector: string | null;
  title: string | null;
  positionStatus: string;
  gradingStatus: string;
};

export type CommunityMembersForAccessParams = {
  search?: string;
  sector?: string;
  company?: string;
  position?: CommunityQuestionnaireFilter;
  grading?: CommunityQuestionnaireFilter;
};

export type CommunityMemberFilterOptions = {
  sectors: string[];
  companies: string[];
};

/** @deprecated — используйте fetchCommunityMembersForAccess */
export type CommunityMemberOption = CommunityMemberForAccess;

export async function fetchCommunityMemberFilterOptions(): Promise<
  { ok: true; data: CommunityMemberFilterOptions } | { ok: false; error: string; status: number }
> {
  const res = await apiFetchJson<{ data: CommunityMemberFilterOptions }>(
    'GET',
    '/private-circle/community/members/filters',
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  const data = res.data.data ?? { sectors: [], companies: [] };
  return {
    ok: true,
    data: {
      sectors: Array.isArray(data.sectors) ? data.sectors : [],
      companies: Array.isArray(data.companies) ? data.companies : [],
    },
  };
}

export async function fetchCommunityMembersForAccess(
  params: CommunityMembersForAccessParams,
): Promise<
  { ok: true; data: CommunityMemberForAccess[]; total: number } | { ok: false; error: string; status: number }
> {
  const qs = new URLSearchParams();
  if (params.search?.trim()) qs.set('search', params.search.trim());
  if (params.sector?.trim()) qs.set('sector', params.sector.trim());
  if (params.company?.trim()) qs.set('company', params.company.trim());
  if (params.position && params.position !== 'all') qs.set('position', params.position);
  if (params.grading && params.grading !== 'all') qs.set('grading', params.grading);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await apiFetchJson<{ data: CommunityMemberForAccess[]; total: number }>(
    'GET',
    `/private-circle/community/members${suffix}`,
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return {
    ok: true,
    data: Array.isArray(res.data.data) ? res.data.data : [],
    total: Number(res.data.total) || 0,
  };
}

export async function searchCommunityMembers(
  search: string,
): Promise<
  { ok: true; data: CommunityMemberForAccess[] } | { ok: false; error: string; status: number }
> {
  const res = await fetchCommunityMembersForAccess({ search });
  if (!res.ok) return res;
  return { ok: true, data: res.data };
}

export type CommunityChannelGrant = {
  userId: string;
  email: string;
  label: string;
  grantedAt: string;
};

export async function fetchCommunityChannelGrants(
  channelId: string,
): Promise<
  { ok: true; data: CommunityChannelGrant[] } | { ok: false; error: string; status: number }
> {
  const res = await apiFetchJson<{ data: CommunityChannelGrant[] }>(
    'GET',
    `/private-circle/community/channels/${encodeURIComponent(channelId)}/grants`,
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true, data: Array.isArray(res.data.data) ? res.data.data : [] };
}

export async function grantCommunityChannelAccess(
  channelId: string,
  userId: string,
): Promise<{ ok: true } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ ok: boolean }>(
    'POST',
    `/private-circle/community/channels/${encodeURIComponent(channelId)}/grants`,
    { body: { userId } },
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true };
}

export async function revokeCommunityChannelAccess(
  channelId: string,
  userId: string,
): Promise<{ ok: true } | { ok: false; error: string; status: number }> {
  const res = await apiFetchJson<{ ok: boolean }>(
    'DELETE',
    `/private-circle/community/channels/${encodeURIComponent(channelId)}/grants/${encodeURIComponent(userId)}`,
  );
  if (!res.ok) return { ok: false, error: res.error, status: res.status };
  return { ok: true };
}
